export const enTenants = {
  title: "Tenants",
  subtitle: "View all tenants in the Olive platform.",
  empty: "No tenants found.",
  actions: {
    newTenant: "Add Tenant",
    clearAllCache: "Clear All Tenants' Cache",
  },
  newTenantModal: {
    title: "Add Tenant",
    closeAria: "Close dialog",
    fields: {
      tenantName: "Tenant name",
    },
    validation: {
      required: "This field is required.",
      invalidFormat:
        "Use lowercase letters, numbers, and hyphens (1–63 characters; no leading or trailing hyphen).",
    },
    actions: {
      cancel: "Cancel",
      save: "Save",
    },
    errors: {
      saveFailed: "Failed to add tenant. Please try again.",
    },
  },
  table: {
    name: "Name",
    status: "Status",
    created: "Created",
    cacheInterval: "Cache Clears Every",
    // Kept although the switches left the grid: the detail page reuses these as aria labels.
    nuggetScheduleAria: "Run nugget configs on their schedules for {{name}}",
    scheduledJobsAria: "Run scheduled jobs for {{name}}",
    cacheIntervalValue: "{{hours}}h {{minutes}}m (UTC)",
    actions: "Actions",
    ariaLabel: "Tenants",
    clearCacheAria: "Clear cache for {{name}}",
    editCacheIntervalAria: "Edit cache clear interval for {{name}}",
    editCacheIntervalTooltip: "Edit Cache clear interval",
  },
  detail: {
    back: "All tenants",
    loading: "Loading tenant…",
    notFound: "Tenant not found",
    createdAt: "Created {{when}}",
    nuggetSchedule: "Nugget Schedules",
    scheduledJobs: "Scheduled Jobs",
    queryCacheHint: "Clears happen on fixed UTC boundaries.",
    sections: {
      scheduling: "Scheduling",
      jobs: "Jobs",
      queryCache: "Query Cache",
      s3Buckets: "S3 Connection Buckets",
    },
  },
  jobs: {
    sectionHint: "Which job types are enabled for this tenant.",
    requiresScheduledJobs: "Turn Scheduled Jobs on to enable this.",
    toggleAria: "Enable {{jobType}} for {{name}}",
    types: {
      classification: {
        label: "Classification Job",
        description:
          "Run a query against a domain and write the results to an S3 connection.",
      },
      learningHistory: {
        label: "Learning History",
        description:
          "Refresh Learning History for every user with new activity since their last run.",
      },
      appSnapshot: {
        label: "App Snapshot Job",
        description:
          "Capture a published app on a schedule, under a name that names its week. Each run re-runs every widget against the warehouse.",
      },
      copy: {
        label: "Copy Job",
        description:
          "Copy JSON files from the tenant's own storage to an S3 connection, all at once or a few files per run.",
      },
    },
  },
  sweepSchedule: {
    loading: "Loading its schedule…",
    notSet: "No schedule set yet.",
    runsAt: "Runs on {{schedule}} (UTC).",
    edit: "Edit schedule",
    placeholder: "0 0 * * *",
    success: "Learning History schedule updated.",
    errors: {
      loadFailed: "Failed to load the Learning History schedule.",
      saveFailed: "Failed to save the Learning History schedule.",
    },
  },
  sweepScheduleModal: {
    title: "Learning History Schedule",
    closeAria: "Close dialog",
    description:
      "When the Learning History refresh runs for {{name}}. Applies from the next scheduler tick; changing it re-arms the job, so a run already pending under the old schedule is dropped.",
    fields: {
      schedule: "Schedule",
      scheduleAria: "Learning History schedule",
      scheduleHelper: "Five-field cron, in UTC. For example 0 0 * * * is every day at midnight.",
    },
    actions: {
      cancel: "Cancel",
      save: "Save",
    },
  },
  cacheIntervalModal: {
    title: "Query Cache Clear Interval",
    closeAria: "Close dialog",
    description:
      "How often {{name}}'s query cache is cleared. Clears happen on fixed UTC boundaries — a 12h 00m interval clears at 12:00 AM and 12:00 PM UTC, and a 0h 10m interval clears at every ten-minute mark.",
    fields: {
      hours: "Hours",
      minutes: "Minutes",
      hoursAria: "Clear interval hours",
      minutesAria: "Clear interval minutes",
      intervalHelper: "Between 0h 05m and 24h 00m.",
      belowMinimum: "The interval must be at least 0h 05m.",
      aboveMaximum: "The interval cannot be more than 24h 00m.",
    },
    actions: {
      cancel: "Cancel",
      save: "Save",
    },
    success: "Cache clear interval updated.",
    errors: {
      saveFailed: "Failed to update the cache clear interval. Please try again.",
    },
  },
  s3Buckets: {
    sectionHint:
      "Buckets this tenant may register as an Amazon S3 connection. An S3 connection has no credentials — the customer grants Olive's role in their own bucket policy, and that role is shared by every tenant, so a bucket has to be listed here before this tenant can use it.",
    empty: "No buckets granted. This tenant cannot create an S3 connection yet.",
    listAria: "Granted buckets for {{name}}",
    fields: {
      bucket: "Bucket name",
      bucketHelper: "Bucket name only — no s3:// prefix and no folder path.",
    },
    actions: {
      grant: "Grant",
      revoke: "Revoke",
      revokeAria: "Revoke {{bucket}}",
    },
    revokeHint:
      "Revoking stops new connections only. A connection already created against the bucket keeps working until it is deleted.",
    success: "Bucket grants updated.",
    validation: {
      invalidBucket:
        "Enter a valid bucket name: 3–63 characters, lowercase letters, numbers, dots and hyphens, starting and ending with a letter or number.",
      duplicate: "That bucket is already granted.",
    },
    errors: {
      loadFailed: "Failed to load bucket grants. Please try again.",
      saveFailed: "Failed to update bucket grants. Please try again.",
    },
  },
  scheduling: {
    nuggetScheduleTooltip:
      "When on, nugget configs for this tenant start runs on their own schedules. Off by default.",
    scheduledJobsTooltip:
      "When on, this tenant's scheduled jobs run on their own schedules. Off by default.",
    success: "Scheduling updated.",
    errors: {
      saveFailed: "Failed to update scheduling. Please try again.",
    },
  },
  status: {
    pending: "Pending",
    provisioning: "Provisioning",
    active: "Active",
    failed: "Failed",
  },
  clearCacheDialog: {
    heading: "Clear cache for this tenant?",
    subheading:
      "This removes all cached API responses and cached query results for this tenant. Users may see a brief slowdown while the cache repopulates.",
    cancel: "Cancel",
    confirm: "Clear Cache",
    success: "Cache cleared for this tenant.",
    errors: {
      clearFailed: "Failed to clear cache. Please try again.",
    },
  },
  clearAllCacheDialog: {
    heading: "Clear cache for all tenants?",
    subheading:
      "This clears cached responses and cached query results for every tenant on the platform and cannot be undone. This is a heavier operation — use only when necessary.",
    cancel: "Cancel",
    confirm: "Clear All",
    success: "Cache cleared for all tenants.",
    errors: {
      clearFailed: "Failed to clear cache. Please try again.",
    },
  },
} as const;
